import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { useTheme } from "@emotion/react";
import { useState } from "react";
import type { FC } from "react";

import {
  normalizeSupportTicketProductionDate,
  normalizeSupportTicketProductQuantity,
} from "./supportTicketProductLabel";
import { SupportTicketProductDialogFields } from "./SupportTicketProductDialogFields";

type ProductUpdate = {
  quantity: number;
  productionDate: Date | null;
  serialNumber: string | null;
};

type Props = {
  productLabel: string;
  productionDate: Date | null;
  serialNumber: string | null;
  quantity: number;
  isOpen: boolean;
  onClose: () => void;
  onRemove: () => void;
  onUpdate: (updates: ProductUpdate) => void;
};

export const SupportTicketProductEditDialog: FC<Props> = ({
  productLabel,
  productionDate,
  serialNumber,
  quantity,
  isOpen,
  onClose,
  onRemove,
  onUpdate,
}) => {
  const theme = useTheme();
  const normalizedQuantity = normalizeSupportTicketProductQuantity(quantity);
  const [productionDateDraft, setProductionDateDraft] = useState<Date | null>(productionDate);
  const [serialDraft, setSerialDraft] = useState(serialNumber ?? "");
  const [quantityDraft, setQuantityDraft] = useState(normalizedQuantity);

  const resetDrafts = () => {
    setProductionDateDraft(productionDate);
    setSerialDraft(serialNumber ?? "");
    setQuantityDraft(normalizedQuantity);
  };

  const handleClose = () => {
    resetDrafts();
    onClose();
  };

  const saveChanges = () => {
    onUpdate({
      quantity: normalizeSupportTicketProductQuantity(quantityDraft),
      productionDate: normalizeSupportTicketProductionDate(productionDateDraft),
      serialNumber: serialDraft.trim() || null,
    });
    onClose();
  };

  const removeProduct = () => {
    onRemove();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog header={productLabel} onClose={handleClose} css={{ width: "22rem" }}>
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <SupportTicketProductDialogFields
          quantity={quantityDraft}
          onQuantityChange={setQuantityDraft}
          productionDate={productionDateDraft}
          onProductionDateChange={setProductionDateDraft}
          serialNumber={serialDraft}
          onSerialNumberChange={setSerialDraft}
        />
        <div css={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <Button variant="text" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            onClick={removeProduct}
            css={{
              background: theme.colors.danger,
              color: "#fff",
              border: "none",
              ":hover": {
                textDecoration: "none",
                opacity: 0.9,
              },
            }}
          >
            Usuń
          </Button>
          <Button onClick={saveChanges}>Zapisz</Button>
        </div>
      </div>
    </Dialog>
  );
};
